/**
 * Data layer for `real-time-click-fraud-detection`. Thin typed fetch wrappers
 * over /api/v1/real-time-click-fraud-detection. Raw `fetch()` is confined to
 * this file per the dash contract (D6).
 *
 * Backend response shape (GET /):
 *   { items: [{ click_id, ip, user_agent, verdict, risk_score, reasons[], created_at }, ...] }
 */

export type FraudVerdict = "clean" | "suspicious" | "fraud";

export interface FraudEventRow {
	click_id: string;
	ip: string;
	user_agent: string;
	verdict: FraudVerdict;
	risk_score: number;
	reasons: string[];
	created_at: number;
}

export interface ListFraudEventsResponse {
	items: FraudEventRow[];
}

const BASE = "/api/v1/real-time-click-fraud-detection";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listFraudEvents(): Promise<ListFraudEventsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: {
			accept: "application/json",
			"x-tenant-id": "t1",
		},
	});
	return parseOrThrow<ListFraudEventsResponse>(res);
}

export const realTimeClickFraudDetectionQueryKeys = {
	all: ["real-time-click-fraud-detection"] as const,
	list: () => [...realTimeClickFraudDetectionQueryKeys.all] as const,
	detail: (id: string) => [...realTimeClickFraudDetectionQueryKeys.all, id] as const,
};
